import React, { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { aiService } from '../services/aiService';
import { useData } from '../contexts/DataContext';
import DomainSelector from '../components/shared/DomainSelector';
import StreamingContainer from '../components/CategoryDescription/StreamingContainer';
import GeneratedContent from '../components/CategoryDescription/GeneratedContent';
import SectionSelection from '../components/CategoryDescription/SectionSelection';
import { useTranslation } from 'react-i18next';
import StatusMessage from '../components/CategoryDescription/StatusMessage';
import { useNotification } from '../contexts/NotificationContext';
import { Domain } from '../types/data';
import { extractErrorMessageFromResponse } from '../services/errorService';
import { CustomerGroupSelector } from '../components/shared/CustomerGroupSelector';
import { FeatureFlag } from '../utils/featureFlags';
import FeatureFlagOverlay from '../components/shared/FeatureFlagOverlay';

const defaltPromptLanguage = 'English';
const defaltPromptToneOfVoice = 'Professional';

const CategoryDescription: React.FC = () => {
  const {
    domains,
    targetCompany,
    customerTargetGroups,
    fetchRelatedDomainData,
    fetchDomains,
    brands,
  } = useData();
  const [activeDomain, setActiveDomain] = useState<Domain | null>(null);
  const [selectedCustomerGroup, setSelectedCustomerGroup] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [categoryUrl, setCategoryUrl] = useState('');
  const [staffFavorites, setStaffFavorites] = useState('');
  const [topPicks, setTopPicks] = useState('');
  const [sectionIntensities, setSectionIntensities] = useState<Record<string, number>>({});
  const [generatedContent, setGeneratedContent] = useState<Record<string, null | string>>({
    meta: null,
    titles: null,
    h1: null,
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [streamingStatus, setStreamingStatus] = useState<Record<string, string>>({});
  const { t } = useTranslation();
  const { showNotification } = useNotification();

  useEffect(() => {
    fetchDomains();
  }, []);

  useEffect(() => {
    loadData();
  }, [domains, targetCompany]);

  const loadData = () => {
    if (targetCompany && domains.length > 0) {
      // Automatically select the first domain if available
      const foundDomain = domains.find((domain: Domain) => domain.company_id === targetCompany);
      if (foundDomain && typeof foundDomain !== 'undefined') {
        setActiveDomain(foundDomain);
        fetchRelatedDomainData(foundDomain.id);
      } else {
        setActiveDomain(null);
      }
    }
  };

  // Check for targetCompany and activeDomain before rendering the main content
  if (!targetCompany || !activeDomain) {
    return (
      <StatusMessage targetCompany={targetCompany} activeDomain={activeDomain?.domain || null} />
    );
  }

  const handleGenerateContent = async () => {
    if (!categoryUrl || !selectedCustomerGroup) {
      showNotification('Please enter a URL and select a customer group', 'error');
      return;
    }
    if (selectedBrand === '' && sectionIntensities['brand'] > 0) {
      showNotification('Please enter a brand', 'error');
      return;
    }
    const sections = { meta: null, titles: null, h1: null } as Record<string, null | string>;
    if (staffFavorites) {
      sectionIntensities['staff_favorites'] = 5;
    }
    if (topPicks) {
      sectionIntensities['top_picks'] = 5;
    }
    Object.keys(sectionIntensities).forEach((section) => {
      sections[section] = null;
    });

    setIsGenerating(true);
    setGeneratedContent(sections);

    try {
      // Create an array of all sections to generate, filtering by intensity
      const allSections = [
        'meta',
        'titles',
        'h1',
        ...Object.entries(sectionIntensities)
          .filter(([, intensity]) => intensity > 0)
          .map(([section]) => section),
      ];

      // Function to process sections in batches
      const processInBatches = async (sections: string[], batchSize: number) => {
        for (let i = 0; i < sections.length; i += batchSize) {
          const batch = sections.slice(i, i + batchSize);
          await Promise.all(batch.map((section) => generateSection(section)));
        }
      };

      // Generate sections in batches of five
      await processInBatches(allSections, 5);
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error generating content:', error);
      showNotification('Error generating content. Please try again.', 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  const generateSection = async (section: string) => {
    setStreamingStatus((prev) => ({ ...prev, [section]: 'generating' }));

    try {
      const trimmedUrl = categoryUrl.trim();
      const payload = {
        url: trimmedUrl,
        section,
        language: activeDomain?.language || defaltPromptLanguage,
        toneOfVoice: activeDomain?.tone_of_voice || defaltPromptToneOfVoice,
        customerGroupDescription:
          customerTargetGroups.find((g) => g.name === selectedCustomerGroup)?.description || '',
        companyId: targetCompany,
        contentLength: sectionIntensities[section] || 0,
        domainName: activeDomain?.domain || '',
        brandDescription: brands?.find((b) => b.id.toString() === selectedBrand)?.description || '',
        staffFavorites: staffFavorites,
        topPicks: topPicks,
      };
      console.log(payload);
      // throw new Error();
      const { data: response, error } = await aiService.generateCategoryDescriptions(payload);
      if (error) {
        throw new Error(await extractErrorMessageFromResponse(error, t));
      }

      // Check if we have a response body
      if (!response.body) {
        throw new Error('No response body received');
      }

      const decoder = new TextDecoder();
      let accumulatedContent = '';

      // Read the response as a stream
      const reader = response.body.getReader();

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          // Decode the chunk
          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                accumulatedContent += data.content;

                setGeneratedContent((prev) => ({
                  ...prev,
                  [section]: accumulatedContent,
                }));
              } catch (e) {
                console.error('Error parsing SSE data:', e);
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }

      setStreamingStatus((prev) => ({ ...prev, [section]: 'completed' }));
    } catch (error) {
      Sentry.captureException(error);
      console.error(`Error generating ${section}:`, error);
      setStreamingStatus((prev) => ({ ...prev, [section]: 'error' }));
      showNotification(
        error instanceof Error ? error.message : 'Error generating content. Please try again.',
        'error'
      );
      throw error;
    }
  };

  return (
    <FeatureFlagOverlay feature={FeatureFlag.CATEGORY_DESCRIPTION}>
      <div className="p-8">
        <h1 className="text-3xl font-bold mb-2">{t('category.category_descriptions')}</h1>
        <div className="mb-6 mt-4">
          {/* Use DomainSelector Component */}
          <DomainSelector activeDomain={activeDomain} setActiveDomain={setActiveDomain} />
        </div>
        {/* Customer Group Selection */}
        <div className="mb-6">
          <CustomerGroupSelector
            customerGroups={customerTargetGroups}
            selectedGroup={selectedCustomerGroup}
            onSelect={setSelectedCustomerGroup}
          />
        </div>

        {/* URL Input */}
        <div className="mb-6">
          <label className="block text-gray-700">{t('category.category_page_url')}:</label>
          <input
            type="text"
            value={categoryUrl}
            onChange={(e) => setCategoryUrl(e.target.value)}
            className="mt-2 w-full p-2 border rounded-md"
            placeholder={t('category.url_placeholder')}
          />
        </div>

        {/* Section Selection */}
        <SectionSelection
          sectionIntensities={sectionIntensities}
          onChange={setSectionIntensities}
          changeBrand={setSelectedBrand}
          changeStaffFavorites={setStaffFavorites}
          changeTopPicks={setTopPicks}
        />

        {/* User Input Sections */}
        {/* <UserInputSections /> */}

        {/* Generate Button */}
        <button
          onClick={handleGenerateContent}
          disabled={isGenerating}
          className="w-full bg-blue-500 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded"
        >
          {isGenerating ? t('category.generating') : t('category.generate_category_content')}
        </button>

        {/* Streaming Status */}
        {isGenerating && (
          <StreamingContainer status={streamingStatus} generatedContent={generatedContent} />
        )}

        {/* Generated Content Display */}
        {Object.values(generatedContent).some((content) => content !== null) && (
          <GeneratedContent content={generatedContent} />
        )}
      </div>
    </FeatureFlagOverlay>
  );
};

export default CategoryDescription;
